/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { inject, InjectionToken } from '@angular/core';
import { ApimProxyConfig } from './models/apim-config.model';
import { ConfigService } from '../config.service';

/**
 * Injection token for APIM proxy configuration
 *
 * This token provides centralized configuration management with environment-based
 * settings and proper dependency injection support.
 *
 * @example
 * ```typescript
 * @Injectable()
 * export class MyService {
 *   constructor(@Inject(APIM_CONFIG_TOKEN) private config: ApimProxyConfig) {}
 * }
 * ```
 *
 * @example Testing override
 * ```typescript
 * TestBed.configureTestingModule({
 *   providers: [
 *     { provide: APIM_CONFIG_TOKEN, useValue: mockApimConfig }
 *   ]
 * });
 * ```
 */
export const APIM_CONFIG_TOKEN = new InjectionToken<ApimProxyConfig>(
  'APIM Proxy Configuration',
  {
    providedIn: 'root',
    factory: () => {
      const configService = inject(ConfigService);
      const config = configService.getConfig();
      // Return configuration from ConfigService
      return {
        baseUrl: config.proxyConfig.baseUrl,
        apimSubscriptionKey: config.proxyConfig.apimSubscriptionKey,
        requestTimeout: 30000 // 30 seconds
      };
    }
  }
);
